import React from "react";
import moment from "moment";

const BARE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const parseBareDate = (date) => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const formatDate = (date) => {
  if (!date) return null;
  let d;
  if (typeof date === "string" && BARE_DATE_RE.test(date)) {
    d = parseBareDate(date);
  } else if (typeof date === "string" && /^\d+$/.test(date)) {
    d = new Date(Number(date));
  } else if (typeof date === "number") {
    d = new Date(date);
  } else {
    d = new Date(date);
  }
  return isNaN(d?.getTime())
    ? null
    : d?.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

export const formatShortWeekdayDateTime = (val) => {
  if (!val) return null;
  let d;
  if (typeof val === "string" && /^\d+$/.test(val)) {
    d = new Date(Number(val));
  } else if (typeof val === "number") {
    d = new Date(val);
  } else {
    d = new Date(val);
  }
  if (isNaN(d.getTime())) return typeof val === "string" ? val : null;
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatDateForInput = (date) => {
  if (!date) return "";
  if (typeof date === "string" && date?.match(/^\d{4}-\d{2}-\d{2}$/))
    return date;
  let d;
  if (typeof date === "string" && /^\d+$/.test(date)) {
    d = new Date(Number(date));
  } else if (typeof date === "number") {
    d = new Date(date);
  } else {
    d = new Date(date);
  }
  if (isNaN(d?.getTime())) return "";
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatDateTime = (date) => {
  if (!date) return null;
  let d;
  if (typeof date === "string" && /^\d+$/.test(date)) {
    d = new Date(Number(date));
  } else if (typeof date === "number") {
    d = new Date(date);
  } else {
    d = new Date(date);
  }
  if (isNaN(d?.getTime())) return null;

  const dateStr = d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const timeStr = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return `${dateStr}, ${timeStr}`;
};

// Formats a timestamp with moment. Default ("Local") uses the viewer's local
// system time zone; pass "UTC" for UTC. Numeric (epoch-ms) strings are parsed
// the same way the rest of the FE does. Returns the parts separately so a cell
// can stack the date over the time, plus a `full` string for tooltips.
export const formatTimestampParts = (val, zone = "Local") => {
  if (!val) return { date: null, time: null, full: null };
  const m = /^\d+$/.test(String(val)) ? moment(Number(val)) : moment(val);
  if (!m.isValid()) return { date: null, time: null, full: null };

  const d = zone === "UTC" ? m.utc() : m.local();
  const date = d.format("DD MMM YYYY");
  const time = d.format("HH:mm");
  return { date, time, full: zone === "UTC" ? `${date}, ${time} UTC` : `${date}, ${time}` };
};

export const useToday = () =>
  React.useMemo(() => {
    const d = new Date();
    const month = String(d?.getMonth() + 1)?.padStart(2, "0");
    const day = String(d?.getDate())?.padStart(2, "0");
    return `${d?.getFullYear()}-${month}-${day}`;
  }, []);

  
// Get start of day (00:00:00) in local timezone, return as UTC ISO string
export const getStartOfDayUTC = (localDate) => {
  if (!localDate) return null;
  const startOfDay = new Date(localDate + "T00:00:00");
  return startOfDay.toISOString();
};

// Get end of day (23:59:59) in local timezone, return as UTC ISO string
export const getEndOfDayUTC = (localDate) => {
  if (!localDate) return null;
  const endOfDay = new Date(localDate + "T23:59:59");
  return endOfDay.toISOString();
};

// Get both start and end of day as UTC range
export const getDateRangeInUTC = (localDate) => {
  return {
    start_time: getStartOfDayUTC(localDate),
    end_time: getEndOfDayUTC(localDate),
  };
};
